import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "../../components/tailwind/button";
import { Card, CardContent } from "../../components/tailwind/card";
import { Badge } from "../../components/tailwind/badge";
import { usePageTitle } from "../../hooks/usePageTitle";
import {
  fetchCampaignsRequest,
  fetchCampaignAnalyticsRequest,
  clearCampaignAnalytics,
} from "../../store/slices/campaignsSlice";
import { getPercentage } from "./constants/campaignConstants";
import { showAlert } from "../../store/alertSlice";
import { buildCampaignFunnelData, buildCampaignStatsData } from "./utils/campaignAnalyticsUtils";
import {
  ArrowLeft,
  Send,
  Calendar,
  Clock,
  Loader2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { whatsappPageShell, waShell } from "./utils/whatsappPageShell";
import { waBtn, waSoft } from "./utils/whatsappUiTokens";
import { AnalyticsSkeleton } from "./components/skeletons/WhatsAppPageSkeletons";

const STATUS_COLORS = {
  completed: cn(waSoft.chip, waSoft.border, "border"),
  running: "bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400 border border-blue-200/60 dark:border-blue-900/40",
  scheduled: "bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400 border border-blue-200/60 dark:border-blue-900/40",
  published: "bg-violet-50 text-violet-700 dark:bg-violet-950/30 dark:text-violet-400 border border-violet-200/60 dark:border-violet-900/40",
  cancelled: "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200/60 dark:border-amber-900/40",
};

const DEFAULT_STATUS_COLOR = "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400 border border-red-200/60 dark:border-red-900/40";

const getFunnelItemPercentage = (label, totals) => {
  switch (label) {
    case "Attempted":
      return "";
    case "Sent":
      return getPercentage(totals.sent, totals.attempted);
    case "Delivered":
      return getPercentage(totals.delivered, totals.sent);
    case "Read":
      return getPercentage(totals.read, totals.delivered);
    case "Failed":
      return getPercentage(totals.failed, totals.attempted);
    default:
      return "";
  }
};

const getFunnelPctClass = (label, totals) => {
  const pct = getFunnelItemPercentage(label, totals);
  if (!pct) return "invisible";
  if (label === "Failed") {
    return totals.failed > 0
      ? "text-red-600 dark:text-red-400"
      : "text-gray-400 dark:text-gray-500";
  }
  return cn(waSoft.text);
};

export default function CampaignAnalytics() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const campaignsList = useSelector((state) => state.campaigns.list) || [];
  const campaignsLoading = useSelector((state) => state.campaigns.loading);
  const {
    analytics,
    analyticsLoading,
    analyticsError,
    lastAnalyticsCampaignId,
  } = useSelector((state) => state.campaigns);

  const campaign = campaignsList.find(
    (c) => String(c.campaign_id) === String(id)
  );

  const campaignStatus = campaign?.status?.toLowerCase();
  const isFailedCampaign = campaignStatus === 'failed';
  const isCancelledCampaign = campaignStatus === 'cancelled';
  const isNoAnalyticsCampaign = isFailedCampaign || isCancelledCampaign;

  useEffect(() => {
    if (isNoAnalyticsCampaign) {
      const message = isCancelledCampaign
        ? 'This campaign has been cancelled and no analytics are available.'
        : 'This campaign has failed and no analytics are available.';
      dispatch(showAlert({ message, type: 'error' }));
      navigate('/whatsapp/campaigns', { replace: true });
    }
  }, [isNoAnalyticsCampaign, isCancelledCampaign, navigate, dispatch]);

  useEffect(() => {
    if (campaignsList.length === 0) {
      dispatch(fetchCampaignsRequest());
    }
  }, [dispatch, campaignsList.length]);

  useEffect(() => {
    if (!id || isNoAnalyticsCampaign) return;
    dispatch(fetchCampaignAnalyticsRequest({ campaignId: id }));
    return () => {
      dispatch(clearCampaignAnalytics());
    };
  }, [dispatch, id, campaign?.created_at, campaign?.completed_at, isNoAnalyticsCampaign]);

  const totals = analytics?.totals || {
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    attempted: 0,
  };

  const funnelData = buildCampaignFunnelData(totals);
  const statsData = buildCampaignStatsData(totals);
  const maxFunnelValue = totals.attempted || totals.sent || 1;

  const campaignName = campaign?.name || "Campaign Analytics";

  const createdDate = campaign?.created_at
    ? new Date(campaign.created_at).toLocaleDateString("en-IN", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "N/A";

  const scheduledDate = campaign?.scheduled_at
    ? new Date(campaign.scheduled_at).toLocaleString("en-IN", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "N/A";

  const channelConfig = (() => {
    try {
      return JSON.parse(campaign?.channel_config?.WHATSAPP || "{}");
    } catch {
      return {};
    }
  })();
  const templateName = channelConfig?.template?.name || "N/A";

  usePageTitle('Campaign Analytics');

  const isInitialLoad =
    (campaignsLoading && campaignsList.length === 0)
    || (analyticsLoading && lastAnalyticsCampaignId !== id)
    || isNoAnalyticsCampaign;

  if (isInitialLoad) {
    return <AnalyticsSkeleton />;
  }

  return (
    <div className={waShell(whatsappPageShell.fluid, "animate-fade-in")}>
      <div className="space-y-6 animate-fade-in-up">
        <div className="flex items-center gap-3">
          <Link to="/whatsapp/campaigns">
            <Button
              variant="ghost"
              size="icon"
              className={cn(waBtn.ghost, "h-10 w-10 shrink-0 rounded-lg")}
              aria-label="Back to campaigns"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="min-w-0 flex flex-wrap items-center gap-2.5">
            <h2 className="text-base sm:text-lg font-semibold tracking-tight text-gray-900 dark:text-white truncate">
              {campaignName}
            </h2>
            {campaign?.status ? (
              <Badge
                className={cn(
                  "text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1 shrink-0 border",
                  STATUS_COLORS[campaign.status.toLowerCase()] || DEFAULT_STATUS_COLOR
                )}
              >
                {campaign.status.toLowerCase() === 'running' && (
                  <Loader2 className="h-3 w-3 animate-spin" />
                )}
                {campaign.status}
              </Badge>
            ) : null}
          </div>
        </div>

        {analyticsError && (
          <Card className="border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/20">
            <CardContent className="p-4 text-sm text-red-700 dark:text-red-300">
              {analyticsError}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
          {statsData.map((stat) => (
            <Card
              key={stat.label}
              className="border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm wa-card-hover"
            >
              <CardContent className="p-4 sm:p-5 text-center">
                <div className={cn("inline-flex p-2.5 rounded-xl mb-3", stat.color)}>
                  <stat.icon className="h-4 w-4" />
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                  {stat.value.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stat.label}</p>
                {stat.pct ? (
                  <Badge
                    variant="secondary"
                    className="mt-2.5 text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-0"
                  >
                    {stat.pct}
                  </Badge>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <Card className="border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
            <CardContent className="p-5 sm:p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Campaign Details</h3>
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-3 py-3 border-b border-gray-100 dark:border-slate-800">
                  <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 shrink-0">
                    <Calendar className="h-4 w-4" /> Created
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white text-right">{createdDate}</span>
                </div>
                {scheduledDate !== "N/A" && (
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-gray-100 dark:border-slate-800">
                    <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 shrink-0">
                      <Clock className="h-4 w-4" /> Scheduled For
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white text-right">{scheduledDate}</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 py-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 shrink-0">
                    <Send className="h-4 w-4" /> Template
                  </span>
                  <span
                    className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[160px] text-right"
                    title={templateName}
                  >
                    {templateName}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2 border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
            <CardContent className="p-5 sm:p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Campaign funnel</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Drop-off between each messaging stage for this campaign.
              </p>
              <div className="h-56 sm:h-64 rounded-lg border border-gray-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/40 px-2 sm:px-4 pt-6 pb-2">
                <div className="flex items-end justify-between h-full gap-1 sm:gap-2">
                  {funnelData.map((item) => {
                    const heightPct = Math.max((item.value / maxFunnelValue) * 100, item.value > 0 ? 6 : 2);
                    const pct = getFunnelItemPercentage(item.label, totals);
                    return (
                      <div key={item.label} className="flex flex-col items-center justify-end flex-1 min-w-0 h-full">
                        <span className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 tabular-nums">
                          {item.value.toLocaleString()}
                        </span>
                        <div
                          className={cn("w-full max-w-[4.5rem] rounded-t-md transition-all duration-500 opacity-90", item.color)}
                          style={{ height: `${heightPct}%`, minHeight: "4px" }}
                        />
                        <div className="mt-3 text-center w-full">
                          <p className="text-[11px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 truncate px-0.5">
                            {item.label}
                          </p>
                          <p className={cn("text-[10px] font-medium mt-0.5 tabular-nums", getFunnelPctClass(item.label, totals))}>
                            {pct || "\u00A0"}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
